import Component from '@glimmer/component'
import t from 'ember-intl/helpers/t'
import Icon from 'customer-flows/components/ui-kit/icon'
import { command, context } from 'customer-flows/utils/core'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { on } from '@ember/modifier'
import uid from 'customer-flows/utils/uid'

// Files in `public/` are copied verbatim and served under the app's base path, so the base has to
// be applied by hand — Vite does not rewrite plain string paths the way it does imported assets.
const LOGO_SRC = `${import.meta.env.BASE_URL}images/bank-of-america.jpg`

export default class CardIssuerDiscount extends Component {
  @context('quote') cardIssuerDiscountApplied: QuoteStore['cardIssuerDiscountApplied']
  @command('quote') enableCardIssuerDiscount: QuoteCommands['enableCardIssuerDiscount']
  @command('quote') disableCardIssuerDiscount: QuoteCommands['disableCardIssuerDiscount']

  @tracked checkedOverride: boolean | null = null
  @tracked isUpdating = false
  @tracked isExpanded = false

  descriptionId = `card-issuer-discount-description-${uid(false)}`
  logoSrc = LOGO_SRC

  // The checkbox has to respond to the click before the quote round-trip settles, so intent wins
  // until the quote catches up. The "discount applied" tag is what confirms the real outcome.
  get isChecked(): boolean {
    return this.checkedOverride ?? this.cardIssuerDiscountApplied
  }

  @action
  toggleExpanded() {
    this.isExpanded = !this.isExpanded
  }

  @action
  async handleChange(checked: boolean) {
    if (this.isUpdating) return

    this.checkedOverride = checked
    this.isUpdating = true

    try {
      if (checked) {
        await this.enableCardIssuerDiscount()
      } else {
        await this.disableCardIssuerDiscount()
      }
      // Once the quote store reflects the change, it becomes the source of truth again —
      // this also lets an externally-triggered disable show up correctly instead of being
      // masked by a stale override.
      this.checkedOverride = null
    } catch {
      this.checkedOverride = null
    } finally {
      this.isUpdating = false
    }
  }

  <template>
    <il-section data-testid='booking__card-issuer-discount' class='@sm/flow:il-section:rounded-3'>
      <div slot='content' class='@container'>
        <div
          class='grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-2'
          data-testid='card-issuer-discount'
        >
          <il-checkbox
            class='col-start-1 row-start-1 [--il-checkbox-disabled-label-opacity:1]'
            data-testid='card-issuer-discount__checkbox'
            checked={{this.isChecked}}
            {{! @glint-ignore: wait until we can't declare custom elements in glint }}
            onChange={{this.handleChange}}
            disabled={{this.isUpdating}}
            align-to-top
          >
            <span slot='label'>
              <span
                class='text-bold-para-16 text-neutrals-350'
                data-testid='card-issuer-discount__title'
              >{{t 'booking.payment.card-issuer-discount.title'}}</span><il-tag
                class='ml-1 align-baseline'
                data-testid='card-issuer-discount__offer-tag'
                label={{t 'booking.payment.card-issuer-discount.offer-tag'}}
                variant='theme-text'
                size='small'
                version='bold'
              />
            </span>
          </il-checkbox>

          <div
            class='col-span-2 col-start-1 row-start-2 flex flex-col items-start gap-2 pl-9
              @min-[460px]:col-span-1'
          >
            <div class='flex w-full items-start gap-2'>
              <div
                id={{this.descriptionId}}
                class='text-reg-para-12 text-neutrals-300
                  {{unless this.isExpanded "line-clamp-2 @min-[460px]:line-clamp-none"}}'
                data-testid='card-issuer-discount__description'
              >
                {{t 'booking.payment.card-issuer-discount.description'}}
                <a
                  href='https://www.peekpro.com/bryant-park-terms-conditions'
                  target='_blank'
                  rel='noopener noreferrer'
                  class='text-bold-para-12 text-neutrals-300'
                  data-testid='card-issuer-discount__terms-link'
                >{{t 'booking.payment.card-issuer-discount.terms-link'}}</a>
              </div>

              <button
                type='button'
                class='shrink-0 text-theme-300 @min-[460px]:hidden'
                data-testid='card-issuer-discount__disclosure-toggle'
                aria-expanded={{if this.isExpanded 'true' 'false'}}
                aria-controls={{this.descriptionId}}
                aria-label={{if
                  this.isExpanded
                  (t 'booking.payment.card-issuer-discount.show-less')
                  (t 'booking.payment.card-issuer-discount.show-more')
                }}
                {{on 'click' this.toggleExpanded}}
              >
                <Icon @src={{if this.isExpanded 'carat-up' 'carat-down'}} />
              </button>
            </div>

            {{#if this.cardIssuerDiscountApplied}}
              <il-tag
                data-testid='card-issuer-discount__applied-tag'
                label={{t 'booking.payment.card-issuer-discount.applied'}}
                variant='success'
                size='small'
                version='bold'
              />
            {{/if}}
          </div>

          <img
            src={{this.logoSrc}}
            alt={{t 'booking.payment.card-issuer-discount.logo-alt'}}
            class='col-start-2 row-start-1 h-12 w-auto shrink-0
              @min-[460px]:row-span-2 @min-[460px]:h-auto @min-[460px]:w-25 @min-[460px]:self-center'
            data-testid='card-issuer-discount__logo'
          />
        </div>
      </div>
    </il-section>
  </template>
}
